import { FC } from 'react'
import { Component } from '../../types/ContentStructure'
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material'
import { getComponent } from '../../utils/utils'

interface ComponentBenefitsAndRequirementsProps {
    component: Component
}

/**
 * BENEFICIOS Y REQUICITOS
 *
 * Estructura BD (productId=8 Crédito PyME):
 *   component (237)
 *     ├─ SECCION 238 → hijos TEXTO: idx0=título+subtítulo, idx1..N=bullets beneficios
 *     ├─ SECCION 243 → hijos TEXTO: idx0=título+subtítulo, idx1..N=bullets requisitos
 *     └─ SECCION 250 → hijos LINK: idx0="Descargar detalles"
 *   component.image = imagen panel de íconos (panel derecho desktop)
 *   component.theme.themeStyles = fondo rojo/oscuro desde CMS
 *
 * Layout mockup:
 *   Desktop: col izq (50%) texto | col der (50%) imagen íconos 3×3
 *   Mobile:  imagen íconos arriba | texto abajo
 */
const ComponentBenefitsAndRequirements: FC<ComponentBenefitsAndRequirementsProps> = ({ component }) => {
    const { image, components, theme, title, imageMd, imageXs } = component
    const muiTheme = useTheme()
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'))

    const secciones = components ? getComponent('SECCION', components) : []
    const secBeneficios = secciones[0]  // SECCION 238
    const secRequisitos = secciones[1]  // SECCION 243
    const secCTAs = secciones[2]  // SECCION 250

    // LINK "Descargar detalles" — primer hijo de secCTAs (componentId=251)
    //const linkDescarga = secCTAs?.components?.[0]

    /* ── Renderiza una sección: título/subtítulo + bullets ── */
    const renderSeccion = (sec: Component | undefined) => {
        if (!sec?.components) return null
        return sec.components.map((item: Component, i: number) => {
            // idx 0 → encabezado de sección (title = "Beneficios", subTitle = "¡Inmediatos!")
            if (i === 0) {
                return (
                    <Box key={i} sx={{
                        mb: 1.5,
                        textAlign: 'justify',
                        maxWidth: '60ch',
                    }}>
                        <Typography
                            variant="h4"
                            component="span"
                            sx={{ fontWeight: 100, color: 'white' }}
                        >
                            {item.title}{' '}
                        </Typography>
                        <Typography
                            variant="h4"
                            component="span"
                            sx={{ fontWeight: 700, color: 'white', fontStyle: 'italic' }}
                        >
                            {item.subTitle}
                        </Typography>
                    </Box>
                )
            }
            // idx > 0 → bullet (TEXTO con text solamente)
            return (
                <Typography
                    key={i}
                    variant="body2"
                    sx={{
                        color: 'white',
                        display: 'flex',
                        alignItems: 'flex-start',
                        textAlign: 'justify',
                        maxWidth: '60ch',
                        gap: 1,
                        mb: 0.75,
                        lineHeight: 1.5,
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                    }}
                >
                    <Box component="span" sx={{ mt: '2px', flexShrink: 0, fontWeight: 700 }}>+</Box>
                    {item.text}
                </Typography>
            )
        })
    }

    return (
        /* Fondo viene del themeStyles del CMS — nunca hardcodeado aquí */
        <Box sx={[{ width: '100%', color: 'white', px: { xs: 0, md: 8 } }]}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                width: '100%',
                //minHeight: { xs: 'auto', md: '480px' },
                backgroundImage: { xs: `url(${imageXs})`, sm: `url(${imageXs})`, md: `url(${imageMd})`, lg: `url(${image})` },
                //backgroundImage: `url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: { xs: 'center', sm: 'center', md: 'right center', lg: 'right center' },
                backgroundRepeat: 'no-repeat',
                minHeight: '80vh',
                justifyContent: { xs: 'flex-end', md: 'center', lg: 'flex-start' },
            }}>

                {/* ── Panel izquierdo: texto Beneficios + Requisitos + CTA ── */}
                <Box sx={{
                    flex: { lg: '0 0 50%' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 3,
                    px: { xs: 3, sm: 4, md: 6 },
                    py: { xs: 4, md: 5 },
                    order: { xs: 2, sm: 2, md: 2, lg: 1 },
                    position: 'relative',
                }}>
                    {/* ── Overlay: gradiente izquierda → transparente ── */}
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        background: {
                            xs: 'linear-gradient(to top, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.40) 50%, rgba(0,0,0,0.0) 100%)',
                            md: 'transparent',
                            lg: 'transparent',
                        },
                        zIndex: 10,
                    }} />
                    <Box sx={{
                        position: 'relative',
                        zIndex: 20,
                    }}>
                        {/* Título */}
                        <Typography variant="h1" sx={{
                            textAlign: { xs: 'center', md: 'justify' },
                            //display: { xs: 'block', md: 'block', lg: 'none' },
                            mb: 2,
                        }}>
                            {title}
                        </Typography>
                        {/* Beneficios */}
                        <Box>{renderSeccion(secBeneficios)}</Box>

                        {/* Requisitos */}
                        <Box>{renderSeccion(secRequisitos)}</Box>

                        {/* Espacio entre secciones */}
                        <Box sx={{ height: 24 }} />

                        {/* CTAs — todos los LINKs dentro de la SECCION CTAs */}
                        {secCTAs?.components && secCTAs.components.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                {secCTAs.components.map((cta: Component, i: number) => cta.title ? (
                                    <Box key={i}>
                                        <Box
                                            component="a"
                                            href={cta.link ?? '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={i === 0 ? {
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 2.5,
                                                py: 1,
                                                borderRadius: 1,
                                                border: '2px solid white',
                                                color: 'white',
                                                textDecoration: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                transition: 'background-color 0.2s',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                                            } : {
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 3,
                                                py: 1.5,
                                                borderRadius: 1,
                                                backgroundColor: 'white',
                                                color: '#001230',
                                                textDecoration: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    transform: 'translateY(-1px)'
                                                },
                                            }}
                                        >
                                            {cta.title}
                                        </Box>
                                    </Box>
                                ) : null)}
                            </Box>
                        )}
                    </Box>

                </Box>

                {/* ── Panel derecho: imagen de íconos ── */}
                {/* {image && (
                    <Box sx={{
                        flex: { md: '0 0 50%' },
                        order: { xs: 1, md: 2 },
                        backgroundImage: `url(${image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        minHeight: { xs: 260, sm: 320, md: 'auto' },
                    }} />
                )} */}
            </Box>
        </Box>
    )
}

export default ComponentBenefitsAndRequirements
